import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { setDriveAnomalyActiveTab } from '../../actions';
import colors from 'theme/_colors.scss';
import useChartResize from 'sharedHooks/useChartResize';
import useUserCapability from 'sharedHooks/useUserCapability';
import { ASSET_TYPES_MS } from 'helpers/constants';
import {
    EnableUniqueScaling,
    PopOutButton,
    ResetZoomButton,
    withLeftButtonGroup,
    withPopOutRow,
    withRightButtonGroup
} from 'sharedComponents/Chart/headers';
import { translate } from 'helpers/translateHelper';
import {
    IconCheckMark
} from 'svgIcons/MotionPortalIcons';
import { chartHeightConstants, driveAnomalyTabs, ACS1000_COOLING_ANOMALY_KEY } from '../../constants';
import { calculateDynamicChartHeight } from '../../helpers';
import { EnergyAnomalyChart } from './EnergyOutlinerAnomaly';
import { CoolingAnomalyChart } from './CoolingAnomalyChart';
import { ACS1000CoolingAnomalyChart } from './ACS1000CoolingAnomalyChart';
import DriveAssetStatusIndicator from './DriveAssetStatusIndicatorComponent';
import DriveAnomalyNavPanel from './DriveAnomalyNavPanelComponent';
import { getAnomalyOverallLevel } from './helpers';
import { getWorstACS1000AnomalySignalDescriptions, getMaxSignalAnomalyLevel } from './ACS1000CoolingAnomalyChart/helpers';
import RetrainingComponent, { RetrainingBanner } from '../MotorAnomalyDetection/RetrainingComponent';
import { RETRAINING_STATUS } from '../MotorAnomalyDetection/constants';
import ChartContextMenuButton from '../MotorAnomalyDetection/ChartContextMenuButton';
import { AnomalyLevels } from './constants';
import './style.scss';


const CoolingAnomalyInfoContent = () =>
    <div className='cooling-anomaly-info'>
        <b className='info-title'>{translate('ABB.Powertrain.Frontend.coolingAnomalyChartTitle')}</b>
        <p className='info-description'>{translate('ABB.Powertrain.Frontend.anomalyDetectInfoText')}</p>
    </div>;

const ACS1000CoolingAnomalyInfoContent = () =>
    <div className='cooling-anomaly-info'>
        <b className='info-title'>{translate('ABB.Powertrain.Frontend.coolingAnomalyChartTitle')}</b>
        <p className='info-description'>{translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalyInfoP1')}</p>
        <p className='info-description'>{translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalyInfoP2')}</p>
        <p className='info-description'>{translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalyInfoP3')}</p>
        <p className='info-description'>{translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalyInfoP4')}</p>
    </div>;

const EnergyAnomalyInfoContent = () =>
    <div className='cooling-anomaly-info'>
        <b className='info-title'>{translate('ABB.Powertrain.Frontend.energyAnomalyChartTitle')}</b>
        <p className='info-description'>{translate('ABB.Powertrain.Frontend.energyAnomalyInfoText')}</p>
    </div>;

const getAcs1000CoolingAnomalySeveritySuffix = (level) => {
    if (level >= AnomalyLevels.level4) {
        return translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalySignalsSevere');
    }
    if (level === AnomalyLevels.level3) {
        return translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalySignalsModerate');
    }
    if (level >= AnomalyLevels.level0) {
        return translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalySignalsMinor');
    }
    return '';
};

const DrivePopOutChartHeader = ({
    retrainingDetails,
    isPrivilegedUser,
    setShowProgressModal,
    CustomHeader,
    headerProps
}) =>
    <div className='anomaly-chart-header-wrapper'>
        <CustomHeader {...headerProps} />
        <RetrainingBanner
            retrainingDetails={retrainingDetails}
            isPrivilegedUser={isPrivilegedUser}
            onViewProgress={() => setShowProgressModal(true)}
        />
    </div>
    ;

DrivePopOutChartHeader.propTypes = {
    retrainingDetails: PropTypes.object,
    isPrivilegedUser: PropTypes.bool,
    setShowProgressModal: PropTypes.func.isRequired,
    CustomHeader: PropTypes.elementType.isRequired,
    headerProps: PropTypes.object
};


const DriveAnomalyDetectionComponent = ({
    coolingAnomalyKPI,
    energyAnomalyKPI,
    loadingStatus,
    assetDetails,
    title,
    levelLabel,
    isZoomed,
    actions,
    selectedAsset,
    initialTab,
    onTabChange
}) => {
    const dispatch = useDispatch();
    const hasCoolingData = !!coolingAnomalyKPI?.conditionTrends?.length;
    const hasEnergyData = !!energyAnomalyKPI?.conditionTrends?.length;
    const showTabs = hasCoolingData && hasEnergyData;

    const [activeDriveAnomalyTab, setActiveDriveAnomalyTab] = useState(
        () => !hasCoolingData && hasEnergyData ? driveAnomalyTabs.ENERGY : driveAnomalyTabs.COOLING
    );

    // Notify parent whenever the active tab changes so the legend can update
    useEffect(() => {
        onTabChange?.(activeDriveAnomalyTab);
    }, [activeDriveAnomalyTab, onTabChange]);

    // One ref per chart so switching tabs never conflicts Highcharts instances
    const containerRef = useRef();
    const coolingChartRef = useRef();
    const energyChartRef = useRef();
    const chartRef = activeDriveAnomalyTab === driveAnomalyTabs.ENERGY ? energyChartRef : coolingChartRef;
    useChartResize(containerRef, chartRef);

    const [chartHeight, setChartHeight] = useState(null);

    // Retraining state management
    const [retrainingModel, setRetrainingModel] = useState(false);
    const [retrainingStartDate, setRetrainingStartDate] = useState(null);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [coolingChartInstance, setCoolingChartInstance] = useState(null);
    const exportMenuRef = useRef(null);

    const isDriveAsset = assetDetails?.details?.assetType?.assetType === ASSET_TYPES_MS.DRIVE;

    // User capability check
    const userCapabilityHelper = useUserCapability();
    const canRetrain = isDriveAsset && userCapabilityHelper.hasDriveAnomalyRetraining();

    const coolingRetrainingDetails = coolingAnomalyKPI?.retrainingDetails;

    const driveRetrainingDetails = isDriveAsset
        ? { ...coolingRetrainingDetails, isDriveAnomaly: true }
        : coolingRetrainingDetails;

    const retrainingStatus = coolingRetrainingDetails?.retrainingStatus;
    const isRetraining = retrainingStatus === RETRAINING_STATUS.TRAINING;
    const chartRetrainingStartDate = coolingRetrainingDetails?.retrainingStartDate ?? null;
    const chartRequestedSubmittedDate = coolingRetrainingDetails?.requestedSubmittedDate ?? null;
    const chartRetrainingCompletedDate = coolingRetrainingDetails?.retrainingCompletedDate ?? null;

    // Sync tab selection when KPI availability changes asynchronously
    useEffect(() => {
        if (hasCoolingData && !hasEnergyData) {
            setActiveDriveAnomalyTab(driveAnomalyTabs.COOLING);
        } else if (hasEnergyData && !hasCoolingData) {
            setActiveDriveAnomalyTab(driveAnomalyTabs.ENERGY);
        }
    }, [hasCoolingData, hasEnergyData]);

    // Navigate to the requested tab when driven by external navigation.
    // Clears the Redux value immediately after consuming it so that switching
    // between Condition Indices and Anomaly Detection does not keep re-applying it.
    useEffect(() => {
        if (!initialTab) { return; }
        if (initialTab === driveAnomalyTabs.ENERGY && hasEnergyData) {
            setActiveDriveAnomalyTab(driveAnomalyTabs.ENERGY);
            dispatch(setDriveAnomalyActiveTab(null));
        } else if (initialTab === driveAnomalyTabs.COOLING && hasCoolingData) {
            setActiveDriveAnomalyTab(driveAnomalyTabs.COOLING);
            dispatch(setDriveAnomalyActiveTab(null));
        }
    }, [initialTab, hasEnergyData, hasCoolingData]);

    const updateChartHeight = useCallback(() => {
        if (chartRef?.current?.chart && containerRef?.current) {
            const containerHeight = containerRef.current.clientHeight || chartHeightConstants.default;
            const activeKPI = activeDriveAnomalyTab === driveAnomalyTabs.ENERGY
                ? energyAnomalyKPI : coolingAnomalyKPI;
            const finalHeight = calculateDynamicChartHeight(
                activeKPI?.conditionTrends,
                true,
                containerHeight
            );
            chartRef.current.chart.update({ chart: { height: finalHeight } }, true, true);
            setChartHeight(finalHeight);
        }
    }, [chartRef, containerRef, activeDriveAnomalyTab, energyAnomalyKPI, coolingAnomalyKPI]);

    useEffect(() => {
        window.addEventListener('resize', updateChartHeight);
        return () => window.removeEventListener('resize', updateChartHeight);
    }, [updateChartHeight]);

    // Close export menu when clicking outside
    useEffect(() => {
        if (!exportMenuOpen) { return; }
        const closeMenu = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', closeMenu);
        return () => document.removeEventListener('mousedown', closeMenu);
    }, [exportMenuOpen]);

    useEffect(() => {
        if (chartRef?.current?.chart) {
            updateChartHeight();
        }
    }, [
        coolingAnomalyKPI?.conditionTrends?.length,
        energyAnomalyKPI?.conditionTrends?.length,
        activeDriveAnomalyTab,
        updateChartHeight
    ]);

    const coolingStatusIndicator = useCallback((headerProps) => {
        const isACS1000 = coolingAnomalyKPI?.key === ACS1000_COOLING_ANOMALY_KEY;
        const anomalyDescriptions = getWorstACS1000AnomalySignalDescriptions(
            isACS1000 ? coolingAnomalyKPI?.conditionTrends : null
        );
        let descriptionText = null;
        if (isACS1000 && anomalyDescriptions?.length > 0) {
            const level = getMaxSignalAnomalyLevel(coolingAnomalyKPI?.conditionTrends);
            const severitySuffix = getAcs1000CoolingAnomalySeveritySuffix(level);
            const last = anomalyDescriptions[anomalyDescriptions.length - 1];
            const rest = anomalyDescriptions.slice(0, -1);
            const joined = rest.length > 0
                ? `${rest.join(', ')} ${translate('ABB.Powertrain.Frontend.acs1000CoolingAnomalySignalsAnd')} ${last}`
                : last;
            descriptionText = `${joined} ${severitySuffix}`;
        }
        return (
            <DriveAssetStatusIndicator
                {...headerProps}
                anomalyDetectionKPI={coolingAnomalyKPI}
                title={title}
                levelLabel={levelLabel}
                getAnomalyOverallLevel={getAnomalyOverallLevel(selectedAsset)}
                anomalyKPIKey={coolingAnomalyKPI?.key}
                InfoContent={isACS1000 ? ACS1000CoolingAnomalyInfoContent : CoolingAnomalyInfoContent}
                descriptionText={descriptionText}
                isRetraining={isRetraining}
                isPrivilegedUser={isACS1000 ? false : canRetrain}
                setRetrainingModel={setRetrainingModel}
            />
        );
    }, [coolingAnomalyKPI, title, levelLabel, selectedAsset, isRetraining, activeDriveAnomalyTab, canRetrain]);


    const energyStatusIndicator = useCallback((headerProps) =>
        <DriveAssetStatusIndicator
            {...headerProps}
            anomalyDetectionKPI={energyAnomalyKPI}
            title={translate('ABB.Powertrain.Frontend.energyAnomalyChartTitle')}
            levelLabel={levelLabel}
            getAnomalyOverallLevel={getAnomalyOverallLevel(selectedAsset)}
            anomalyKPIKey={energyAnomalyKPI?.key}
            InfoContent={EnergyAnomalyInfoContent}
            isRetraining={false}
            isPrivilegedUser={false}
            setRetrainingModel={() => { }}
        />
        , [energyAnomalyKPI, levelLabel, selectedAsset, isRetraining, activeDriveAnomalyTab, canRetrain]);

    const activeIndicator = activeDriveAnomalyTab === driveAnomalyTabs.ENERGY
        ? energyStatusIndicator
        : coolingStatusIndicator;

    const chartContextMenuButtonWithProps = useCallback((headerProps) =>
        <ChartContextMenuButton
            {...headerProps}
            chartRef={chartRef}
            exportMenuRef={exportMenuRef}
            exportMenuOpen={exportMenuOpen}
            setExportMenuOpen={setExportMenuOpen}
        />
        , [chartRef, exportMenuRef, exportMenuOpen, setExportMenuOpen]);

    const CustomHeader = useMemo(() => withPopOutRow([
        withLeftButtonGroup([activeIndicator, EnableUniqueScaling]),
        withRightButtonGroup([ResetZoomButton, PopOutButton, chartContextMenuButtonWithProps])
    ]), [activeIndicator, chartContextMenuButtonWithProps]);

    // Retraining Banner component
    const retrainingBanner = useMemo(() =>
        <RetrainingBanner
            retrainingDetails={driveRetrainingDetails}
            isPrivilegedUser={canRetrain}
            onViewProgress={() => setShowProgressModal(true)}
        />
        , [driveRetrainingDetails, canRetrain]);

    // Renders the retraining banner inside the popout window header (cooling chart only)
    const drivePopOutChartHeaderWithProps = useCallback((headerProps) =>
        <DrivePopOutChartHeader
            headerProps={headerProps}
            retrainingDetails={driveRetrainingDetails}
            isPrivilegedUser={canRetrain}
            setShowProgressModal={setShowProgressModal}
            CustomHeader={CustomHeader}
        />
        , [driveRetrainingDetails, canRetrain, setShowProgressModal, CustomHeader]);

    // Scenario 4: no data at all
    if (!hasCoolingData && !hasEnergyData) {
        return (
            <div className='anomaly-detect-empty-message'>
                <IconCheckMark width={'32px'} height={'32px'} color={colors.nero} />
                <span>{translate('ABB.Powertrain.Frontend.anomalyDetectEmptyLabel')}</span>
            </div>
        );
    }

    const isEnergyTabActive = activeDriveAnomalyTab === driveAnomalyTabs.ENERGY;
    const isACS1000Cooling = coolingAnomalyKPI?.key === ACS1000_COOLING_ANOMALY_KEY;

    let activeChart;
    if (isEnergyTabActive) {
        activeChart = <EnergyAnomalyChart
            loadingStatus={loadingStatus}
            energyAnomalyKPI={energyAnomalyKPI}
            assetDetails={assetDetails}
            chartRef={energyChartRef}
            mainChartHeader={CustomHeader}
            conditionTrendsData={energyAnomalyKPI?.conditionTrends}
            title={energyAnomalyKPI?.name}
            isZoomed={isZoomed}
            containerRef={containerRef}
            actions={actions}
            chartHeight={chartHeight}
        />;
    } else if (isACS1000Cooling) {
        activeChart = <ACS1000CoolingAnomalyChart
            coolingAnomalyKPI={coolingAnomalyKPI}
            loadingStatus={loadingStatus}
            assetDetails={assetDetails}
            isZoomed={isZoomed}
            actions={actions}
            chartRef={coolingChartRef}
            chartHeight={chartHeight}
            mainChartHeader={CustomHeader}
            containerRef={containerRef}
        />;
    } else {
        activeChart = <CoolingAnomalyChart
            coolingAnomalyKPI={coolingAnomalyKPI}
            loadingStatus={loadingStatus}
            assetDetails={assetDetails}
            isZoomed={isZoomed}
            actions={actions}
            chartRef={coolingChartRef}
            chartHeight={chartHeight}
            mainChartHeader={CustomHeader}
            popOutChartHeader={drivePopOutChartHeaderWithProps}
            containerRef={containerRef}
            mainChartBanner={retrainingBanner}
            retrainingStartDate={chartRetrainingStartDate}
            requestedSubmittedDate={chartRequestedSubmittedDate}
            retrainingCompletedDate={chartRetrainingCompletedDate}
            retrainingStatus={retrainingStatus}
            chartInstance={coolingChartInstance}
            onChartReady={setCoolingChartInstance}
        />;
    }

    return (
        <div className='anomaly-detection-wrapper drive-anomaly-detection-wrapper'>
            <div className='anomaly-detection-container' ref={containerRef}>
                {showTabs
                    ? <div className='drive-anomaly-tab-layout'>
                        <DriveAnomalyNavPanel
                            activeDriveAnomalyTab={activeDriveAnomalyTab}
                            setActiveDriveAnomalyTab={setActiveDriveAnomalyTab}
                            energyAnomalyKPI={energyAnomalyKPI}
                            coolingAnomalyKPI={coolingAnomalyKPI}
                        />
                        <div className='drive-anomaly-divider' />
                        <div className='drive-anomaly-chart-panel'>
                            {activeChart}
                        </div>
                    </div>
                    : activeChart}
            </div>
            <RetrainingComponent
                retrainingModel={retrainingModel}
                setRetrainingModel={setRetrainingModel}
                actions={actions}
                selectedAsset={selectedAsset}
                retrainingStartDate={retrainingStartDate}
                setRetrainingStartDate={setRetrainingStartDate}
                showProgressModal={showProgressModal}
                setShowProgressModal={setShowProgressModal}
                retrainingDetails={driveRetrainingDetails}
            />
        </div>
    );
};

DriveAnomalyDetectionComponent.propTypes = {
    coolingAnomalyKPI: PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        unitGroupId: PropTypes.number,
        valueType: PropTypes.string,
        conditionTrends: PropTypes.array,
        condition: PropTypes.string,
        currentCondition: PropTypes.string,
        retrainingDetails: PropTypes.shape({
            enableRetraining: PropTypes.bool,
            retrainingStatus: PropTypes.string,
            retrainingStartDate: PropTypes.string,
            retrainingCompletedDate: PropTypes.string,
            requestedSubmittedDate: PropTypes.string,
            initiatedBy: PropTypes.string,
            isDriveAnomaly: PropTypes.bool
        })
    }),
    energyAnomalyKPI: PropTypes.shape({
        key: PropTypes.string,
        name: PropTypes.string,
        conditionTrends: PropTypes.array,
        condition: PropTypes.string,
        currentCondition: PropTypes.string
    }),
    loadingStatus: PropTypes.string,
    assetDetails: PropTypes.object,
    selectedAsset: PropTypes.object,
    actions: PropTypes.shape({
        removeSelectedKpiLegend: PropTypes.func,
        setIsZoomed: PropTypes.func,
        reloadConditionInsights: PropTypes.func
    }),
    title: PropTypes.string,
    levelLabel: PropTypes.string,
    isZoomed: PropTypes.bool,
    initialTab: PropTypes.string,
    onTabChange: PropTypes.func
};

export default DriveAnomalyDetectionComponent;
